import { useState, useEffect } from 'react';

export type DeviceType = 'mobile' | 'tablet' | 'desktop';

interface DeviceInfo {
  type: DeviceType;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  width: number;
}

/**
 * Hook per rilevare il tipo di dispositivo in base alla larghezza dello schermo
 *
 * Breakpoints:
 * - Mobile: < 768px
 * - Tablet: >= 768px && < 1024px
 * - Desktop: >= 1024px
 *
 * @returns {DeviceInfo} Informazioni sul dispositivo corrente
 */
export const useDeviceType = (): DeviceInfo => {
  const getDeviceType = (width: number): DeviceType => {
    if (width < 768) return 'mobile';
    if (width < 1024) return 'tablet';
    return 'desktop';
  };

  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>(() => {
    const width = typeof window !== 'undefined' ? window.innerWidth : 1024;
    const type = getDeviceType(width);

    return {
      type,
      isMobile: type === 'mobile',
      isTablet: type === 'tablet',
      isDesktop: type === 'desktop',
      width,
    };
  });

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const type = getDeviceType(width);

      setDeviceInfo({
        type,
        isMobile: type === 'mobile',
        isTablet: type === 'tablet',
        isDesktop: type === 'desktop',
        width,
      });
    };

    // Usa ResizeObserver se disponibile per migliori performance
    let resizeObserver: ResizeObserver | null = null;

    if (typeof window === 'undefined') {
      // Server-side rendering - do nothing
      return () => {};
    }

    if (window.ResizeObserver) {
      resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(document.body);
    } else {
      window.addEventListener('resize', handleResize);
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      } else {
        window.removeEventListener('resize', handleResize);
      }
    };
  }, []);

  return deviceInfo;
};

/**
 * Hook semplificato per controllare solo se siamo su mobile
 */
export const useIsMobile = (): boolean => {
  const { isMobile } = useDeviceType();
  return isMobile;
};

/**
 * Hook per verificare se la larghezza dello schermo è minore di un valore specifico
 */
export const useMediaQuery = (maxWidth: number): boolean => {
  const [matches, setMatches] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth < maxWidth;
    }
    return false;
  });

  useEffect(() => {
    const handleResize = () => {
      setMatches(window.innerWidth < maxWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [maxWidth]);

  return matches;
};
